import React, { useState } from "react";import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MagnifyingGlass, CheckCircle, Warning, Info, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * A GSTIN input with an inline "Verify & Fetch" action. On success, calls
 * `onResolved({state, state_code, trade_name, legal_name, address, pincode})`.
 * The parent form decides how to apply — usually auto-fill state + state_code and
 * offer the fetched company name as a one-click option alongside manual entry.
 */
export default function GstinLookupField({
  value, onChange, onResolved, label = "GSTIN", disabled,
  testid = "gstin-input",
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [autoTried, setAutoTried] = useState("");   // remembers last auto-verified value

  const verify = async () => {
    const gstin = (value || "").trim().toUpperCase();
    if (!gstin) { toast.error("Enter a GSTIN first"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/gstin/verify", { gstin });
      setResult(data);
      if (data.state_code) {
        onResolved?.(data);
        toast.success(`Resolved · ${data.state} (${data.state_code})`);
      } else {
        toast.error(data.reason || "Could not resolve GSTIN");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  // Auto-verify once when the field hits 15 chars (valid GSTIN length)
  React.useEffect(() => {
    const g = (value || "").trim().toUpperCase();
    if (g.length === 15 && g !== autoTried && !loading) {
      setAutoTried(g);
      verify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="eyebrow">{label}</Label>
        {result && (
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest">
            {result.valid ? (
              <span className="text-success flex items-center gap-1" style={{ color: "hsl(var(--success))" }}>
                <CheckCircle size={11} weight="fill" /> Valid format
              </span>
            ) : (
              <span className="text-warning flex items-center gap-1" style={{ color: "hsl(var(--warning))" }}>
                <Warning size={11} weight="fill" /> Check format
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder="15-character GSTIN, e.g. 33ABCDE1234F1Z5"
          disabled={disabled}
          className="font-mono uppercase"
          maxLength={15}
          data-testid={testid}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), verify())}
        />
        <Button
          type="button"
          onClick={verify}
          variant="outline"
          disabled={loading || disabled}
          className="h-10 shrink-0"
          data-testid={`${testid}-verify-btn`}
          title="Fetch state & company details from GSTIN"
        >
          {loading
            ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" />
            : <MagnifyingGlass size={13} className="mr-1.5" />}
          {loading ? "Fetching…" : "Fetch"}
        </Button>
      </div>
      {result && (
        <div className="mt-1.5 rounded-md border border-border bg-secondary/50 p-2.5 text-[12px] space-y-1">
          {result.source === "derived" && result.external_error && (
            <div className="mb-1.5 rounded bg-red-50 border border-red-200 px-2 py-1.5 text-[11px] text-red-700">
              <b>Auto-fetch failed:</b> {result.external_error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            <div><span className="eyebrow mr-1.5">State:</span><span className="font-medium">{result.state || "—"}</span></div>
            <div><span className="eyebrow mr-1.5">Code:</span><span className="font-mono font-medium">{result.state_code || "—"}</span></div>
          </div>
          {(result.trade_name || result.legal_name) && (
            <div className="pt-1.5 border-t border-border/70 mt-1">
              {result.trade_name && (
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="eyebrow mr-1.5">Trade Name:</span>
                    <span className="font-medium">{result.trade_name}</span>
                  </div>
                  <Button
                    type="button" size="sm" variant="outline"
                    className="h-6 text-[10px] px-2"
                    onClick={() => onResolved?.({ ...result, apply_name: result.trade_name })}
                    data-testid={`${testid}-use-trade`}
                  >
                    Use this name
                  </Button>
                </div>
              )}
              {result.legal_name && result.legal_name !== result.trade_name && (
                <div className="flex items-center justify-between gap-2 mt-1">
                  <div>
                    <span className="eyebrow mr-1.5">Legal Name:</span>
                    <span className="font-medium">{result.legal_name}</span>
                  </div>
                  <Button
                    type="button" size="sm" variant="outline"
                    className="h-6 text-[10px] px-2"
                    onClick={() => onResolved?.({ ...result, apply_name: result.legal_name })}
                    data-testid={`${testid}-use-legal`}
                  >
                    Use this name
                  </Button>
                </div>
              )}
              {result.address && (
                <div className="mt-1">
                  <span className="eyebrow mr-1.5">Address:</span>
                  <span>{result.address}{result.pincode ? ` — ${result.pincode}` : ""}</span>
                </div>
              )}
              {result.status && (
                <div className="mt-1">
                  <span className="eyebrow mr-1.5">Status:</span>
                  <span className="uppercase font-medium">{result.status}</span>
                </div>
              )}
            </div>
          )}
          {result.source === "derived" && (
            <div className="pt-1.5 border-t border-border/70 mt-1 flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <Info size={12} className="shrink-0 mt-0.5" />
              <span>
                {result.external_available
                  ? "External API returned no company data — state resolved from GSTIN prefix. You can type the company name manually."
                  : "State auto-derived from GSTIN prefix. To auto-fill company name & address for any GSTIN, add GSTIN_API_URL and GSTIN_API_KEY to backend/.env."}
              </span>
            </div>
          )}
          {result.source === "demo" && (
            <div className="pt-1.5 border-t border-border/70 mt-1 flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <Info size={12} className="shrink-0 mt-0.5" />
              <span>Auto-filled from built-in demo dataset. <b>All fields below remain editable</b> — you can tweak name, address, state or anything else before saving. Configure GSTIN_API_URL & GSTIN_API_KEY for live data on any GSTIN.</span>
            </div>
          )}
          {result.source === "external_api" && (
            <div className="pt-1.5 border-t border-border/70 mt-1 flex items-start gap-1.5 text-[11px] text-success" style={{ color: "hsl(var(--success))" }}>
              <CheckCircle size={12} weight="fill" className="shrink-0 mt-0.5" />
              <span>Live data from GSTIN verification API. <b>All fields below are editable</b> — adjust name, address, state or anything else before saving.</span>
            </div>
          )}
          {result.source === "cache" && (
            <div className="pt-1.5 border-t border-border/70 mt-1 flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <Info size={12} className="shrink-0 mt-0.5" />
              <span>
                Served from cache (looked up earlier, kept for {result.cache_days || 30} days to save API credits).
                <b> All fields below remain editable.</b>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
